import { Logger } from '@halo/utils';
import { createCloudFactory } from '@/scripts/cloud';
import { userApis } from '@/configs/apis';
import { debugLevel } from '@/scripts/debug';
import type {
  Account,
  AuthInfo,
  Cloud,
  CloudPlatform,
  DeleteAccountArgs,
  DeleteCloudDataArgs,
  SaveAccountArgs,
  uploadArgs,
} from '../cloud/types';
import { CloudAccountType } from '../cloud/types';
import { type GoogleInitClientArgs, type AppleInitClientArgs } from '../cloud/types';
import type { GoogleCloud } from '../cloud/googleCloud';
import type { AppleCloud } from '../cloud/appleCloud';
import { formatWalletData } from '../cloud/helper';
import { createWallet, asymmetricEncryption, getWalletInfo } from './helper';

export { CloudAccountType };
export interface ConfigParams {
  type: CloudAccountType;
}

export const enum EVENT_NAME {
  LOGIN_PROGRESS = 'loginProgress',
}

interface CloudInfo {
  cid: string;
  picture: string;
  platform: CloudPlatform;
}

export enum WalletInfoType {
  NEW = 'new',
  LOGIN = 'login',
  SYNC_CREATE = 'syncCreate',
  SYNC_MERGE = 'syncMerge',
  MERGE = 'merge',
}

export interface MasterAccount {
  uid: string;
  clientUid: string;
  isDefault: boolean;
}

export interface WalletInfo {
  type?: WalletInfoType;
  trace_id: string;
  t: number;
  updateTime: number;
  isChange: boolean;
  password: string;
  uid: string;
  clientUid: string;
  accountId: string;
  walletAddress: string;
  accounts: Account[];
  invalidAccounts: Account[];
  authInfo: AuthInfo;
  cloudInfo: Partial<CloudInfo>;
}

export class WalletCloud {
  private static instance: WalletCloud | null = null;
  outgoing = new Map<string, { resolve: (value: any) => void; reject: (reason?: any) => void }>();
  eventListenerMap: { [eventName: string]: Array<(value: any) => void> } = {};
  cloud!: Cloud;
  config!: ConfigParams & (GoogleInitClientArgs | AppleInitClientArgs);
  logger!: Logger;

  constructor(options: ConfigParams & (GoogleInitClientArgs | AppleInitClientArgs)) {
    if (WalletCloud.instance && options.type === WalletCloud.instance.config.type) {
      return WalletCloud.instance;
    }
    this.config = options;
    WalletCloud.instance = this;
    this.logger = new Logger('Login', Boolean(debugLevel));
    return WalletCloud.instance;
  }

  on = (eventName: EVENT_NAME, callback: (value: any) => void) => {
    if (!Array.isArray(this.eventListenerMap[eventName])) {
      this.eventListenerMap[eventName] = [];
    }
    this.eventListenerMap[eventName].push(callback);
  };

  off = (eventName: string, callback: (value: any) => void) => {
    const events = this.eventListenerMap[eventName] || [];
    this.eventListenerMap[eventName] = events.filter((event) => event !== callback);
  };

  private emit = (eventName: EVENT_NAME, data: any) => {
    const events = this.eventListenerMap[eventName] || [];
    events.forEach((event) => event(data));
  };

  init = async (params: { cid?: string; authInfo: AuthInfo; clientParams?: any }) => {
    const { type, clientParams, ...rest } = this.config;
    const { clientParams: clientParams_, ...restParams } = params;

    this.cloud = createCloudFactory(type);

    return await this.cloud.initClient({
      ...rest,
      ...restParams,
      clientParams: { ...clientParams, ...clientParams_ },
    });
  };

  login = async (params: { authInfo: AuthInfo; clientParams?: any }) => {
    this.emit(EVENT_NAME.LOGIN_PROGRESS, { status: 1 });
    this.logger.log('login init', { params });
    const authInfo = await this.init(params);

    this.emit(EVENT_NAME.LOGIN_PROGRESS, { status: 2, isNew: false });
    this.logger.log('login get cloud account', { params });

    const cloudAccountData = await this.getCloudAccount();

    const { accounts, cloudInfo, t } = cloudAccountData;
    this.logger.log('login check user', { cloudAccountData });

    const { user_id, trace_id } = await userApis.checkUserSub({
      account_type: cloudInfo?.platform,
      sub_id: cloudInfo?.cid,
      token: authInfo?.access_token,
    });

    if (user_id) {
      if (accounts.length === 0) {
        this.logger.log('login has user_id no account', { cloudAccountData, user_id });
        return { type: WalletInfoType.SYNC_CREATE, trace_id, authInfo, cloudInfo };
      } else {
        const newWalletData = getWalletInfo({
          ...cloudAccountData,
          authInfo,
          updateTime: t,
        });
        const isEnqual = newWalletData.uid === user_id;
        this.logger.log('login has user_id and account', { newWalletData, user_id, isEnqual });

        if (isEnqual) {
          return { type: WalletInfoType.LOGIN, trace_id, ...newWalletData };
        } else {
          return {
            type: WalletInfoType.SYNC_MERGE,
            trace_id,
            ...cloudAccountData,
            authInfo,
          };
        }
      }
    } else {
      if (accounts.length === 0) {
        this.emit(EVENT_NAME.LOGIN_PROGRESS, { status: 2, isNew: true });
        this.emit(EVENT_NAME.LOGIN_PROGRESS, { status: 3 });

        const newWalletData = await this.createNewWallet();
        this.logger.log('login no user_id and account', { newWalletData, user_id });

        return { type: WalletInfoType.NEW, trace_id, ...newWalletData, authInfo, cloudInfo };
      } else {
        this.logger.log('login no user_id has account', { cloudAccountData, user_id });
        return {
          type: WalletInfoType.MERGE,
          trace_id,
          ...cloudAccountData,
          authInfo,
        };
      }
    }
  };

  requestAuthRedirect = async () => {
    const { type, clientParams } = this.config;
    this.cloud = createCloudFactory(type);
    this.cloud.requestAuthRedirect?.(clientParams);
  };

  cloudKitSetUpAuth = async (params: { authInfo: AuthInfo }) => {
    await this.init(params);
    return (this.cloud as AppleCloud).cloudKitSetUpAuth();
  };

  getCloudAccount = async () => {
    if (!this.cloud.authInfo) {
      throw {
        _ckErrorCode: 'AUTHENTICATION_REQUIRED',
      };
    }

    const userInfo = await this.cloud.getUserInfo();
    if (!userInfo?.id) {
      throw Error('no user info');
    }

    const cloudInfo = {
      cid: asymmetricEncryption(userInfo.id),
      picture: userInfo.picture,
      platform: this.cloud.platform,
    };

    const cloudData = await this.cloud.getCloudData({
      cid: cloudInfo.cid,
    });

    const { wallet } = cloudData;

    return {
      ...wallet,
      isChange: !!wallet?.isChange,
      accounts: wallet?.accounts || [],
      invalidAccounts: wallet?.invalidAccounts || [],
      cloudInfo,
    };
  };

  createNewWallet = async (params?: { password?: string }) => {
    const newCloudAccount = await formatWalletData({
      password: params?.password || crypto.randomUUID(),
      isChange: false,
      accounts: [],
    });

    const newAccount = await createWallet({
      password: newCloudAccount.password,
    });

    const newWalletData = {
      ...newCloudAccount,
      accounts: [newAccount],
    };

    return getWalletInfo({ ...newWalletData, updateTime: newWalletData.t });
  };

  mergeWalletAccounts = async (params: { password: string; accounts: Account[] }) => {
    const { password = crypto.randomUUID(), accounts } = params;

    const newAccount = await createWallet({ password });

    const oldTempAccounts = accounts?.map((account) => {
      if (account.isDefault || account.uid) {
        const { isDefault, uid, clientUid, name, ...rest } = account;
        return {
          name: name === 'Halo Wallet' ? 'Halo Wallet(1)' : name,
          ...rest,
        };
      }
      return account;
    });

    const newCloudData = await formatWalletData({
      isChange: false,
      password,
      accounts: [newAccount, ...oldTempAccounts],
    });
    return getWalletInfo({ ...newCloudData, updateTime: newCloudData.t });
  };

  uploadNewWallet = async (wallet: uploadArgs['data']['wallet']) => {
    return (this.cloud as GoogleCloud)?.upload?.({ data: { wallet } });
  };

  updateWalletAccounts = (params: {
    t?: number;
    isChange?: boolean;
    password?: string;
    accounts?: Account[];
  }) => {
    return (this.cloud as GoogleCloud)?.updateWalletAccounts?.(params);
  };
  updateWalletAccount = async (params: { account: Partial<Account> }) => {
    return (this.cloud as GoogleCloud)?.updateWalletAccount?.(params);
  };
  saveWalletAccount = async (params: SaveAccountArgs) => {
    return (this.cloud as GoogleCloud)?.saveWalletAccount?.(params);
  };
  deleteWalletAccount = async (params: DeleteAccountArgs) => {
    return (this.cloud as GoogleCloud)?.deleteWalletAccount?.(params);
  };
  getWalletAccounts = async () => {
    return (this.cloud as GoogleCloud)?.getWalletAccounts?.();
  };
  getCloudData = async () => {
    return (this.cloud as GoogleCloud)?.getCloudData();
  };
  updateCloudData = async (params: uploadArgs['data']) => {
    return (this.cloud as GoogleCloud)?.upload?.({ data: params });
  };
  deleteCloudData = async (params: DeleteCloudDataArgs) => {
    const res = await (this.cloud as GoogleCloud)?.deleteCloudData?.(params);
    this.cloud.logout();
    return res;
  };
  getUserInfo = async () => {
    return (this.cloud as GoogleCloud)?.getUserInfo?.();
  };
}
